import React, { createContext, useContext, useState, useEffect } from 'react';

interface ThemeContextType {
  darkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  darkMode: true,
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [darkMode, setDarkMode] = useState(true);

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('darkMode', String(next));
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const savedDarkMode = localStorage.getItem('darkMode');

    if (savedTheme) {
      const isDark = savedTheme === 'dark';
      setDarkMode(isDark);
      localStorage.setItem('darkMode', String(isDark));
    } else if (savedDarkMode !== null) {
      const isDark = savedDarkMode === 'true';
      setDarkMode(isDark);
      localStorage.setItem('theme', isDark ? 'dark' : 'light');
    } else {
      // Sem preferência salva, força tema escuro por padrão
      setDarkMode(true);
      localStorage.setItem('darkMode', 'true');
      localStorage.setItem('theme', 'dark');
    }
  }, []);

  // Sincroniza a classe/data-attribute no documento sempre que o tema mudar
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
      document.body.classList.toggle('dark-mode', darkMode);
    }
  }, [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};